import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { segments as segmentsApi, concerns as concernsApi, photos as photosApi, agreementFiles as filesApi, accessTypes as atApi, users as usersApi } from '../services/api';
import { formatFeet } from '../utils/units';
import { footingColor, footingDescription, FOOTING_SCORES } from '../utils/footing';
import {
  categoryLabel, EXPANSION_STATUSES, CONTACT_METHODS, contactMethodLabel,
} from '../utils/categories';
import { segmentName } from '../utils/segments';

const STATUS_LABELS = {
  clear: '✅ Clear', light_weeds: '🌱 Light Weeds', heavy_weeds: '🌿 Heavy Weeds',
  other_maintenance: '🔧 Other Maintenance Needed', closed_impassable: '🚫 Closed / Impassable',
};

export default function SegmentPanel({ segment, onClose, onUpdate }) {
  const { user, isVolunteer, isAdmin, isChair, effectiveRole } = useAuth();
  const isNeighbor = effectiveRole === 'neighbor';
  const isTrailView = segment.isTrailView;

  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [accessTypes, setAccessTypes] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [docFile, setDocFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  // Expansion segments: landowner outreach log.
  const isExpansion = segment.category === 'expansion';
  const today = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, local
  const [contactForm, setContactForm] = useState(null);
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    if (!isVolunteer || !isExpansion || volunteers.length) return;
    usersApi.listVolunteers().then(setVolunteers)
      .catch(err => console.error('Could not load the volunteer roster:', err));
  }, [isVolunteer, isExpansion, volunteers.length]);

  // Footing is the trail owner's call (or a chair's or admin's); others see it read-only.
  const canRateFooting = isAdmin || isChair || (!!segment.assigned_volunteer_id && segment.assigned_volunteer_id === user?.id);

  const handleFootingChange = async (value) => {
    try {
      await segmentsApi.update(segment.id, { footing_quality: value === '' ? null : Number(value) });
      if (onUpdate) onUpdate();
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const handleExpansionStatus = async (expansion_status) => {
    try {
      await segmentsApi.update(segment.id, { expansion_status });
      if (onUpdate) onUpdate();
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const handleAddContact = async () => {
    setSavingContact(true);
    try {
      await segmentsApi.addExpansionContact(segment.id, contactForm);
      setContactForm(null);
      if (onUpdate) onUpdate();
    } catch (err) { setMessage(`Error: ${err.message}`); }
    finally { setSavingContact(false); }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Delete this contact entry?')) return;
    try {
      await segmentsApi.deleteExpansionContact(id);
      if (onUpdate) onUpdate();
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const fmtCommentDate = (d) =>
    new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  const handleAddComment = async () => {
    const body = newComment.trim();
    if (!body) return;
    setPostingComment(true);
    try {
      await segmentsApi.addCommitteeComment(segment.id, body);
      setNewComment('');
      if (onUpdate) onUpdate();
    } catch (err) { setMessage(`Error: ${err.message}`); }
    finally { setPostingComment(false); }
  };

  const handleDeleteComment = async (id) => {
    if (!window.confirm('Delete this committee comment?')) return;
    try {
      await segmentsApi.deleteCommitteeComment(id);
      if (onUpdate) onUpdate();
    } catch (err) { setMessage(`Error: ${err.message}`); }
  };

  const startEditing = async () => {
    setEditData({
      description: segment.description || '',
      apn: segment.apn || '',
      address: segment.address || '',
      landowner_name: segment.landowner_name || '',
      contact_email: segment.contact_email || '',
      contact_phone: segment.contact_phone || '',
      agreement_ref: segment.agreement_ref || '',
      access_type_id: segment.access_type_id || '',
      parcel_sale_date: segment.parcel_sale_date ? segment.parcel_sale_date.slice(0, 10) : '',
      agreement_date: segment.agreement_date ? segment.agreement_date.slice(0, 10) : '',
      assigned_volunteer_id: segment.assigned_volunteer_id || '',
      length_feet: segment.length_feet || '',
      surface_type: segment.surface_type || '',
      difficulty: segment.difficulty || '',
      maintenance_status: segment.maintenance_status || 'clear',
      category: segment.category || 'internal',
      expansion_status: segment.expansion_status || '',
    });
    try { setAccessTypes(await atApi.list()); } catch {}
    // Don't swallow this silently — an empty dropdown looked like a UI bug
    // for months when it was really a 403 on the roster call.
    try { setVolunteers(await usersApi.listVolunteers()); }
    catch (err) { console.error('Could not load the volunteer roster:', err); }
    setEditing(true);
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const data = { ...editData };
      // Convert empty strings to null for optional fields
      Object.keys(data).forEach(k => { if (data[k] === '') data[k] = null; });
      await segmentsApi.update(segment.id, data);
      setMessage('Saved!');
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) { setMessage(`Error: ${err.message}`); }
    finally { setSubmitting(false); }
  };

  const handleDocUpload = async () => {
    if (!docFile) return;
    setSubmitting(true);
    try {
      await filesApi.upload(segment.id, docFile);
      setMessage('Document uploaded!');
      setDocFile(null);
      if (onUpdate) onUpdate();
    } catch (err) { setMessage(`Error: ${err.message}`); }
    finally { setSubmitting(false); }
  };

  const formatApn = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 9);
    if (digits.length > 6) return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
    if (digits.length > 3) return `${digits.slice(0,3)}-${digits.slice(3)}`;
    return digits;
  };

  const ed = (field) => ({
    value: editData[field] || '',
    onChange: e => setEditData({ ...editData, [field]: e.target.value }),
    style: { width: '100%', padding: '0.3rem', fontSize: '0.85rem', border: '1px solid #ced4da', borderRadius: '4px', marginTop: '0.15rem' },
  });

  // ── Trail aggregate view for neighbors ──────────────────────────
  if (isTrailView) {
    return (
      <div className="segment-panel">
        <div className="segment-panel-header">
          <div><h2>{segment.trail_name}</h2></div>
          <button onClick={onClose} className="btn-close">✕</button>
        </div>
        <div className="segment-panel-body">
          {segment.description && <p>{segment.description}</p>}
          <div className="segment-meta">
            {segment.total_length_feet > 0 && <span>📏 {formatFeet(segment.total_length_feet)} total</span>}
            {segment.difficulty && <span>⛰️ {segment.difficulty}</span>}
            {segment.segment_count && <span>📍 {segment.segment_count} segments</span>}
          </div>
          {segment.photos && segment.photos.length > 0 && (
            <div className="segment-photos">
              <h4>Photos</h4>
              <div className="photo-grid">
                {segment.photos.map(p => (
                  <div key={p.id} className="photo-thumb">
                    <img src={p.url || `/uploads/photos/${p.file_path}`} alt={p.caption || 'Trail photo'} />
                    {p.caption && <span className="photo-caption">{p.caption}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Segment-level view (volunteer/admin) ────────────────────────
  return (
    <div className="segment-panel">
      <div className="segment-panel-header">
        <div>
          <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
            <h2 style={{margin: 0}}>{segmentName(segment, ' — Segment ')}</h2>
            {isVolunteer && segment.id && !editing && (
              <button className="btn-sm btn-secondary" style={{fontSize: '0.75rem', whiteSpace: 'nowrap'}} onClick={startEditing}>Edit</button>
            )}
          </div>
          {segment.category && segment.category !== 'internal' && (
            <span className={`category-badge category-${segment.category}`}>{categoryLabel(segment.category)}</span>
          )}
        </div>
        <button onClick={onClose} className="btn-close">✕</button>
      </div>

      {message && (
        <div className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}<button onClick={() => setMessage(null)}>✕</button>
        </div>
      )}

      <div className="segment-panel-body">
        {/* ── Edit mode ── */}
        {editing ? (
          <div className="segment-edit-inline">
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem'}}>
              {isAdmin && (
                <label style={{fontSize: '0.8rem', fontWeight: 500}}>Category
                  <select {...ed('category')}>
                    <option value="internal">Internal</option>
                    <option value="external">Roadside</option>
                    <option value="courtesy">Courtesy</option>
                    <option value="expansion">Expansion (proposed)</option>
                  </select>
                </label>
              )}
              {editData.category === 'expansion' && (
                <label style={{fontSize: '0.8rem', fontWeight: 500}}>Expansion Status
                  <select {...ed('expansion_status')}>
                    {EXPANSION_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>
              )}
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Description
                <textarea {...ed('description')} rows={2} />
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Access Type
                <select {...ed('access_type_id')}>
                  <option value="">— None —</option>
                  {accessTypes.map(at => <option key={at.id} value={at.id}>{at.name}</option>)}
                </select>
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Agreement Ref
                <input type="text" {...ed('agreement_ref')} />
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>APN
                <input type="text" value={editData.apn || ''} onChange={e => setEditData({...editData, apn: formatApn(e.target.value)})}
                  placeholder="xxx-xxx-xxx" style={{width: '100%', padding: '0.3rem', fontSize: '0.85rem', border: '1px solid #ced4da', borderRadius: '4px', marginTop: '0.15rem'}} />
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Address
                <input type="text" {...ed('address')} />
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Landowner
                <input type="text" {...ed('landowner_name')} />
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Contact Email
                <input type="email" {...ed('contact_email')} />
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Contact Phone
                <input type="tel" {...ed('contact_phone')} />
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Parcel Sale Date
                <input type="date" {...ed('parcel_sale_date')} />
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Agreement Date
                <input type="date" {...ed('agreement_date')} />
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Trail Owner
                <select {...ed('assigned_volunteer_id')}>
                  <option value="">— None —</option>
                  {volunteers.map(v => <option key={v.id} value={v.id}>{v.display_name}</option>)}
                </select>
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Length (ft)
                <input type="number" step="1" {...ed('length_feet')} />
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Surface
                <select {...ed('surface_type')}>
                  <option value="">—</option><option value="dirt">Dirt</option><option value="gravel">Gravel</option>
                  <option value="paved">Paved</option><option value="mixed">Mixed</option><option value="other">Other</option>
                </select>
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Difficulty
                <select {...ed('difficulty')}>
                  <option value="">—</option><option value="easy">Easy</option><option value="moderate">Moderate</option><option value="difficult">Difficult</option>
                </select>
              </label>
              <label style={{fontSize: '0.8rem', fontWeight: 500}}>Status
                <select {...ed('maintenance_status')}>
                  <option value="clear">Clear</option><option value="light_weeds">Light Weeds</option>
                  <option value="heavy_weeds">Heavy Weeds</option><option value="other_maintenance">Other Maintenance</option>
                  <option value="closed_impassable">Closed/Impassable</option>
                </select>
              </label>
            </div>
            <div style={{marginTop: '0.75rem', display: 'flex', gap: '0.5rem'}}>
              <button className="btn-sm btn-primary" onClick={handleSave} disabled={submitting}>
                {submitting ? 'Saving...' : 'Save'}
              </button>
              <button className="btn-sm btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            {/* ── Read-only view ── */}
            <div className="segment-info">
              <p>{segment.description}</p>
              <div className="segment-meta">
                {segment.length_feet && <span>📏 {formatFeet(segment.length_feet)}</span>}
                {segment.surface_type && <span>🪨 {segment.surface_type}</span>}
                {segment.difficulty && <span>⛰️ {segment.difficulty}</span>}
              </div>
              <div className="segment-status">
                <strong>Status:</strong> {STATUS_LABELS[segment.maintenance_status] || segment.maintenance_status}
              </div>
              {isVolunteer && (
                <div style={{marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem'}}>
                    <strong>Footing:</strong>
                    <span className="footing-dot" style={{backgroundColor: footingColor(segment.footing_quality)}} />
                    {canRateFooting ? (
                      <select value={segment.footing_quality ?? ''} onChange={e => handleFootingChange(e.target.value)}
                        title="10 = anyone could walk it easily · 1 = challenging for horses and people"
                        style={{padding: '0.2rem', fontSize: '0.85rem', border: '1px solid #ced4da', borderRadius: '4px'}}>
                        <option value="">Not rated</option>
                        {FOOTING_SCORES.map(n => <option key={n} value={n}>{n} — {footingDescription(n)}</option>)}
                      </select>
                    ) : (
                      <span title="10 = anyone could walk it easily · 1 = challenging for horses and people">
                        {segment.footing_quality ? `${segment.footing_quality} — ${footingDescription(segment.footing_quality)}` : 'Not rated'}
                      </span>
                    )}
                  </label>
                  {segment.footing_quality && segment.footing_rated_by_name && (
                    <span style={{fontSize: '0.8rem', color: '#6c757d'}}>
                      by {segment.footing_rated_by_name} on {new Date(segment.footing_rated_at).toLocaleDateString()}
                    </span>
                  )}
                  {!canRateFooting && (
                    <span style={{fontSize: '0.8rem', color: '#6c757d', flexBasis: '100%'}}>
                      {segment.assigned_volunteer_name
                        ? `Only the trail owner (${segment.assigned_volunteer_name}), a chair or an admin can change this.`
                        : 'No trail owner yet — a chair or admin can score it, or assign an owner.'}
                    </span>
                  )}
                </div>
              )}
              {segment.assigned_volunteer_name && (
                <p><strong>Trail Owner:</strong> {segment.assigned_volunteer_name}</p>
              )}
              {isVolunteer && (
                <div style={{marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem'}}>
                    <input type="checkbox" checked={!!segment.audit_2026}
                      onChange={async () => {
                        try {
                          await segmentsApi.toggleAudit(segment.id);
                          if (onUpdate) onUpdate();
                        } catch (err) { setMessage(`Error: ${err.message}`); }
                      }} />
                    <strong>2026 Audit</strong>
                  </label>
                  {segment.audit_2026 && segment.audit_2026_by_name && (
                    <span style={{fontSize: '0.8rem', color: '#6c757d'}}>
                      by {segment.audit_2026_by_name} on {new Date(segment.audit_2026_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}
              {segment.has_active_concern && <div className="concern-badge">⚠️ Active concern reported</div>}
            </div>

            {/* Expansion: status + landowner outreach log (volunteer+) */}
            {isVolunteer && isExpansion && (
              <div className="segment-private expansion-box">
                <h4>Proposed Expansion (Volunteer Only)</h4>
                <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem'}}>
                  <strong>Status:</strong>
                  <select value={segment.expansion_status || 'identified'}
                    onChange={e => handleExpansionStatus(e.target.value)}
                    style={{padding: '0.2rem', fontSize: '0.85rem', border: '1px solid #ced4da', borderRadius: '4px'}}>
                    {EXPANSION_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>

                <h4 style={{marginTop: '0.75rem'}}>Landowner Contacts</h4>
                {(segment.expansion_contacts || []).length === 0 && (
                  <p style={{color: '#6c757d', fontSize: '0.85rem', margin: '0 0 0.5rem'}}>No one has contacted the owner yet.</p>
                )}
                {(segment.expansion_contacts || []).map(c => (
                  <div key={c.id} className="expansion-contact">
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                      <span>
                        <strong>{c.contacted_by_name || 'Unknown'}</strong>
                        {' · '}{new Date(`${String(c.contacted_on).slice(0, 10)}T00:00:00`).toLocaleDateString()}
                        {c.method && <> · {contactMethodLabel(c.method)}</>}
                      </span>
                      {(isAdmin || isChair || c.created_by === user?.id) && (
                        <button onClick={() => handleDeleteContact(c.id)} title="Delete contact"
                          style={{border: 'none', background: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '0.8rem', padding: 0}}>✕</button>
                      )}
                    </div>
                    {c.notes && <p style={{whiteSpace: 'pre-wrap', margin: '0.15rem 0 0'}}>{c.notes}</p>}
                  </div>
                ))}

                {contactForm ? (
                  <div className="expansion-contact-form">
                    <label style={{fontSize: '0.8rem'}}>Contacted by
                      <select value={contactForm.contacted_by}
                        onChange={e => setContactForm({ ...contactForm, contacted_by: e.target.value })}>
                        {!volunteers.some(v => v.id === user?.id) && user && <option value={user.id}>{user.display_name}</option>}
                        {volunteers.map(v => <option key={v.id} value={v.id}>{v.display_name}</option>)}
                      </select>
                    </label>
                    <label style={{fontSize: '0.8rem'}}>Date
                      <input type="date" value={contactForm.contacted_on} max={today()}
                        onChange={e => setContactForm({ ...contactForm, contacted_on: e.target.value })} />
                    </label>
                    <label style={{fontSize: '0.8rem'}}>How
                      <select value={contactForm.method}
                        onChange={e => setContactForm({ ...contactForm, method: e.target.value })}>
                        <option value="">—</option>
                        {CONTACT_METHODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </label>
                    <span />
                    <textarea rows={2} placeholder="What was discussed, next steps…" value={contactForm.notes}
                      onChange={e => setContactForm({ ...contactForm, notes: e.target.value })} />
                    <div style={{gridColumn: '1 / -1', display: 'flex', gap: '0.5rem'}}>
                      <button className="btn-sm btn-primary" onClick={handleAddContact}
                        disabled={savingContact || !contactForm.contacted_on}>
                        {savingContact ? 'Saving...' : 'Save contact'}
                      </button>
                      <button className="btn-sm btn-secondary" onClick={() => setContactForm(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn-sm btn-primary" style={{marginTop: '0.25rem'}}
                    onClick={() => setContactForm({ contacted_by: user?.id || '', contacted_on: today(), method: '', notes: '' })}>
                    + Log a contact
                  </button>
                )}
              </div>
            )}

            {/* Volunteer: private info + access type */}
            {isVolunteer && (segment.apn || segment.address || segment.agreement_ref || segment.landowner_name || segment.contact_email || segment.contact_phone || segment.parcel_sale_date || segment.agreement_date || segment.access_type_name) && (
              <div className="segment-private">
                <h4>Property Details (Volunteer Only)</h4>
                {segment.access_type_name && (
                  <p><strong>Access Type:</strong>{' '}
                    <span className="access-type-badge" style={{ borderLeftColor: segment.access_type_color || '#ccc' }}>
                      {segment.access_type_name}
                    </span>
                  </p>
                )}
                {segment.agreement_ref && <p><strong>Agreement Ref:</strong> {segment.agreement_ref}</p>}
                {segment.apn && <p><strong>APN:</strong> {segment.apn}</p>}
                {segment.address && <p><strong>Address:</strong> {segment.address}</p>}
                {segment.landowner_name && <p><strong>Landowner:</strong> {segment.landowner_name}</p>}
                {segment.contact_email && <p><strong>Email:</strong> {segment.contact_email}</p>}
                {segment.contact_phone && <p><strong>Phone:</strong> {segment.contact_phone}</p>}
                {segment.parcel_sale_date && <p><strong>Parcel Sale Date:</strong> {new Date(segment.parcel_sale_date).toLocaleDateString()}</p>}
                {segment.agreement_date && <p><strong>Agreement Date:</strong> {new Date(segment.agreement_date).toLocaleDateString()}</p>}
              </div>
            )}

            {/* Committee Comments */}
            {isVolunteer && (
              <div className="segment-private">
                <h4>Committee Comments</h4>
                {(segment.committee_comment_thread || []).length === 0 && (
                  <p style={{color: '#6c757d', fontSize: '0.85rem', margin: '0 0 0.5rem'}}>No comments yet.</p>
                )}
                {(segment.committee_comment_thread || []).map(c => (
                  <div key={c.id} style={{marginBottom: '0.6rem'}}>
                    <p style={{whiteSpace: 'pre-wrap', margin: 0}}>{c.body}</p>
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.4rem'}}>
                      <em style={{fontSize: '0.8rem', color: '#6c757d'}}>
                        — {c.author_name || 'Unknown'}, {fmtCommentDate(c.created_at)}
                      </em>
                      {(isAdmin || isChair || c.author_id === user?.id) && (
                        <button onClick={() => handleDeleteComment(c.id)} title="Delete comment"
                          style={{border: 'none', background: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '0.8rem', padding: 0}}>✕</button>
                      )}
                    </div>
                  </div>
                ))}
                <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
                  placeholder="Add a committee comment..." rows={2}
                  style={{width: '100%', padding: '0.3rem', fontSize: '0.85rem', border: '1px solid #ced4da', borderRadius: '4px', marginTop: '0.25rem'}} />
                <button className="btn-sm btn-primary" style={{marginTop: '0.35rem'}}
                  onClick={handleAddComment} disabled={postingComment || !newComment.trim()}>
                  {postingComment ? 'Adding...' : 'Add comment'}
                </button>
              </div>
            )}
          </>
        )}

        {/* Agreement Files (volunteer+) */}
        {isVolunteer && segment.agreement_files && segment.agreement_files.length > 0 && (
          <div className="segment-files">
            <h4>Agreement Documents</h4>
            {segment.agreement_files.map(f => {
              const localUrl = filesApi.downloadUrl(f.file_path);
              const link = localUrl ? (
                <a href={localUrl} target="_blank" rel="noopener noreferrer" className="file-link" style={{flex: 1}}>
                  📄 {f.file_name} {f.file_size > 0 && <span className="file-size">({Math.round(f.file_size / 1024)}KB)</span>}
                </a>
              ) : (
                <a href="#" className="file-link" style={{flex: 1}} onClick={async (e) => {
                  e.preventDefault();
                  const url = await filesApi.getDownloadUrl(f.id);
                  window.open(url, '_blank');
                }}>
                  📄 {f.file_name}
                </a>
              );
              return editing ? (
                <div key={f.id} style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  {link}
                  <button className="btn-sm btn-danger" style={{padding: '0.1rem 0.4rem', fontSize: '0.7rem', flexShrink: 0}}
                    onClick={async () => {
                      if (!window.confirm('Remove this document?')) return;
                      try {
                        await filesApi.delete(f.id);
                        setMessage('Document removed');
                        if (onUpdate) onUpdate();
                      } catch (err) { setMessage(`Error: ${err.message}`); }
                    }}>✕</button>
                </div>
              ) : (
                <React.Fragment key={f.id}>{link}</React.Fragment>
              );
            })}
          </div>
        )}

        {/* Upload agreement document (volunteer+) */}
        {isVolunteer && !isTrailView && (
          <div className="segment-action">
            <h4>Upload Agreement Document</h4>
            <input type="file" accept=".pdf" onChange={e => setDocFile(e.target.files[0] || null)} />
            {docFile && (
              <div>
                <p style={{ fontSize: '0.9rem', color: '#6c757d' }}>Selected: {docFile.name}</p>
                <button className="btn-upload" onClick={handleDocUpload} disabled={submitting}>
                  {submitting ? 'Uploading...' : '📄 Upload PDF'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Photos */}
        {segment.photos && segment.photos.length > 0 && (
          <div className="segment-photos">
            <h4>Photos</h4>
            <div className="photo-grid">
              {segment.photos.map(p => (
                <div key={p.id} className="photo-thumb">
                  <img src={p.url || `/uploads/photos/${p.file_path}`} alt={p.caption || 'Trail photo'} />
                  {p.caption && <span className="photo-caption">{p.caption}</span>}
                  {isVolunteer && p.status === 'pending' && <span className="photo-pending-badge">Pending</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Concerns list */}
        {segment.concerns && segment.concerns.length > 0 && (
          <div className="segment-concerns">
            <h4>Concerns</h4>
            {segment.concerns.map(c => (
              <div key={c.id} className={`concern-item concern-${c.status}`}>
                <div className="concern-header">
                  <span className="concern-status">{c.status}</span>
                  <span className="concern-author">{c.raised_by_name}</span>
                  <span className="concern-date">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <p>{c.description}</p>
                {c.resolution_notes && <p className="concern-resolution">Resolution: {c.resolution_notes}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Maintenance log */}
        {isVolunteer && segment.maintenance_logs && segment.maintenance_logs.length > 0 && (
          <div className="segment-logs">
            <h4>Maintenance Log</h4>
            {segment.maintenance_logs.map(log => (
              <div key={log.id} className="log-entry">
                <span className="log-date">{new Date(log.created_at).toLocaleDateString()}</span>
                <span className="log-author">{log.logged_by_name}</span>
                <span>{STATUS_LABELS[log.old_status]} → {STATUS_LABELS[log.new_status]}</span>
                {log.notes && <p className="log-notes">{log.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
